import {
  type SandpackCodeEditor,
  useSandpack,
  useSandpackTheme,
} from '@codesandbox/sandpack-react';
import { ChevronRightSquare } from 'lucide-react';
import { useRef } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { IconButton } from '#app/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#app/components/ui/tooltip';
import { usePrettierKeyMap } from '#app/hooks/dune-keymaps/use-prettier-key-maps';
import {
  type ProjectSchemaModel,
  type FullProjectSchemaModel,
} from '#app/utils/sandpack';

type CodeMirrorRef = React.ComponentRef<typeof SandpackCodeEditor>;

type DuneEmbedWrapperProps = ProjectSchemaModel & {
  children: (config: {
    editorRef: React.MutableRefObject<CodeMirrorRef | null>;
  }) => JSX.Element;
  paid?: FullProjectSchemaModel['paid'];
};

export const DuneEmbedWrapper: React.FC<DuneEmbedWrapperProps> = (props) => {
  const { sandpack } = useSandpack();
  const { theme } = useSandpackTheme();
  const editorRef = useRef<CodeMirrorRef>(null);
  const { formatCode } = usePrettierKeyMap({ editorRef });

  const wrapperColor = theme.colors.surface1;
  const fontColor = theme.colors.base;

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex items-center justify-between rounded-t-lg py-2"
        style={{ backgroundColor: wrapperColor, color: fontColor }}
      >
        <div className="flex-1 pl-8" />
        {props.paid?.removeBranding ? null : (
          <a
            href={props.paid?.customBrandLink || 'https://kempo.io'}
            target="_blank"
            className="font-showcase text-xl font-black"
            rel="noreferrer"
          >
            {props.paid?.customBrandText || 'Kempo'}
          </a>
        )}
        <div className="mt-0.5 flex h-full flex-1 justify-end gap-6 pr-8">
          <ClientOnly fallback={null}>
            {() => (
              <>
                {props.display.general.enableFormatting && (
                  <HeaderButton
                    label="Format Code"
                    primaryColor={fontColor}
                    secondaryColor={wrapperColor}
                    onClick={() => void formatCode()}
                  >
                    <ChevronRightSquare className="size-5" />
                  </HeaderButton>
                )}
              </>
            )}
          </ClientOnly>
        </div>
      </div>

      <div
        // Manually set the `sandpack.lazyAnchorRef`
        // to avoid infinite loading loop
        // TODO: https://github.com/codesandbox/sandpack/issues/851#issuecomment-1479502726
        ref={sandpack.lazyAnchorRef}
        className="flex-1 overflow-hidden rounded-b-lg border-4 border-t-0"
        style={{ borderColor: wrapperColor }}
      >
        <ClientOnly fallback={null}>
          {() => props.children({ editorRef })}
        </ClientOnly>
      </div>
    </div>
  );
};

type HeaderButtonProps = React.PropsWithChildren<{
  label: string;
  primaryColor: string;
  secondaryColor: string;
  onClick: () => void;
}>;

const HeaderButton = ({
  label,
  primaryColor,
  secondaryColor,
  onClick,
  children,
}: HeaderButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <IconButton
          label={label}
          aria-label={label}
          className="border-none bg-transparent outline-none hover:scale-110 hover:bg-transparent focus:scale-110 active:scale-110"
          style={{ color: primaryColor }}
          onClick={onClick}
        >
          {children}
        </IconButton>
      </TooltipTrigger>

      <TooltipContent
        style={{ backgroundColor: primaryColor, color: secondaryColor }}
        align="end"
        side="bottom"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
};
